import classNames from "classnames"

import CellData from "./ComparisonTable/CellData"
import TableHeaders from "./ComparisonTable/TableHeaders"
import { Color } from "../../../constants/V2/color"
import { useIsBreakpoint } from "../../../utils/V2/screen"

import { usePageLayoutState } from "@utils/layout"
import { getBackgroundColorClass } from "@utils/V2/color"
import React, { useRef, useMemo, useState, useEffect } from "react"

const MAX_DESKTOP_COLUMNS_LIMIT = 3
const MAX_MOBILE_COLUMNS_LIMIT = 2

type Props = {
  columnImages?: string[]
  rowHeadings: string[]
  data: string[][]
  backgroundColor: Color.Black | Color.Charcoal | Color.White | Color.MintGreen
  className?: string
}

const DoubleEntryComparisonTable = ({
  columnImages,
  rowHeadings,
  data,
  backgroundColor,
  className,
}: Props) => {
  const { hasAnchorNavigation } = usePageLayoutState()
  const tableTheme =
    backgroundColor === Color.Black || backgroundColor === Color.Charcoal
      ? "dark"
      : "light"
  const titleRowIndices = useRef<number[]>([])

  const columnCount = useMemo(() => {
    return Math.max(...data.map((row) => row.length))
  }, [data])

  const maxDesktopColumns = Math.min(columnCount, MAX_DESKTOP_COLUMNS_LIMIT)
  const maxMobileColumns = MAX_MOBILE_COLUMNS_LIMIT
  const isMobileView = useIsBreakpoint("mobile")

  const initialSelectedColumns = useMemo(() => {
    const availableColumns = Array.from({ length: columnCount }, (_, i) => i)
    return availableColumns.slice(
      0,
      isMobileView ? maxMobileColumns : maxDesktopColumns
    )
  }, [columnCount, maxDesktopColumns, maxMobileColumns, isMobileView])

  const [selectedColumns, setSelectedColumns] = useState<number[]>(
    initialSelectedColumns
  )

  useEffect(() => {
    const availableColumns = Array.from({ length: columnCount }, (_, i) => i)
    setSelectedColumns(
      availableColumns.slice(
        0,
        isMobileView ? maxMobileColumns : maxDesktopColumns
      )
    )
  }, [columnCount, maxDesktopColumns, maxMobileColumns, isMobileView])
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null
  )

  // Get columns that are not currently selected (for dropdown options)
  const availableColumns = useMemo(() => {
    return Array.from({ length: columnCount }, (_, i) => i).filter(
      (columnIndex) => !selectedColumns.includes(columnIndex)
    )
  }, [columnCount, selectedColumns])

  const toggleDropdown = (columnIndex: number) => {
    setOpenDropdownIndex(openDropdownIndex === columnIndex ? null : columnIndex)
  }

  const selectColumn = (
    newColumnIndex: number,
    replacingColumnIndex: number
  ) => {
    setSelectedColumns((prev) => {
      const newSelected = [...prev]
      const currentColumnIndex = newSelected[replacingColumnIndex]

      newSelected[replacingColumnIndex] = newColumnIndex

      const duplicateIndex = newSelected.findIndex(
        (p, i) => p === newColumnIndex && i !== replacingColumnIndex
      )
      if (duplicateIndex !== -1) {
        newSelected[duplicateIndex] = currentColumnIndex
      }

      return newSelected
    })
    setOpenDropdownIndex(null)
  }

  return (
    <div
      className={classNames(
        className,
        "grid grid-cols-1 gap-x-16 md:gap-x-20 lg:gap-x-32"
      )}
    >
      <div
        className={classNames(
          getBackgroundColorClass(backgroundColor),
          "sticky z-10 rounded-b-10 py-16",
          "before:absolute before:inset-x-0 before:bottom-full before:h-8 before:[background-color:inherit]",
          hasAnchorNavigation ? "top-[69px]" : "top-navHeight-xs",
          "grid grid-cols-2",
          columnCount < 3 ? "sm:grid-cols-2" : "sm:grid-cols-3",
          {
            "!rounded-l-none": openDropdownIndex === 0,
          }
        )}
      >
        <TableHeaders
          selectedColumns={selectedColumns}
          data={data}
          columnImages={columnImages}
          openDropdownIndex={openDropdownIndex}
          availableColumns={availableColumns}
          tableTheme={tableTheme}
          backgroundColor={backgroundColor}
          maxMobileColumns={maxMobileColumns}
          maxDesktopColumns={maxDesktopColumns}
          onToggleDropdown={toggleDropdown}
          onSelectColumn={selectColumn}
        />
      </div>

      {data.slice(1).map((_, rowIndex) => (
        <div
          key={`row-${rowIndex + 1}`}
          className={classNames(
            "z-1 grid grid-flow-row items-center",
            `grid-cols-${Math.min(selectedColumns.length, maxMobileColumns)} sm:grid-cols-${Math.min(selectedColumns.length, maxDesktopColumns)}`
          )}
        >
          {selectedColumns.map((columnIndex, displayIndex) => (
            <CellData
              key={`column-${columnIndex}-display-${displayIndex}`}
              rowIndex={rowIndex + 1}
              columnIndex={columnIndex}
              displayIndex={displayIndex}
              data={data}
              rowHeadings={rowHeadings}
              tableTheme={tableTheme}
              backgroundColor={backgroundColor}
              maxMobileColumns={maxMobileColumns}
              maxDesktopColumns={maxDesktopColumns}
              titleRowIndices={titleRowIndices}
              variant="doubleEntry"
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export default DoubleEntryComparisonTable
